<script setup lang="ts">
import type Tech from '@/types/Tech.ts'
import { computed, onMounted, ref } from 'vue'
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { CDN } from '@/models/cdn.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Backend from '@/models/backend.ts'
import Eta from '@/models/eta.ts'
import { pluralize } from '@/models/tools.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const gameStore = useGameStore()
const assetStore = useAssetStore()

/**
 * Props
 */
const props = defineProps<{
  tech: Tech
}>()

/**
 * Variables
 */
const top = ref(0)
const left = ref(0)

/**
 * Functions
 */
const setTech = () => {
  if (gameStore.playerTechQueue.queue?.includes(props.tech.id)) {
    gameStore.modal2Type = 'confirmationDelete'
    gameStore.modal2Data = {
      title: t('research.queue.removeTitle'),
      message: t('research.queue.removeMessage'),
      actionText: t('research.queue.dequeue'),
      cancelText: t('common.cancel'),
      alternativeText: t('research.queue.researchNow'),
      action: () => {
        const backend = new Backend()
        const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
        const index = currentQueue.indexOf(props.tech.id)
        if (index > -1) {
          currentQueue.splice(index, 1)
          backend.updateTechQueue(currentQueue).then(() => {
            gameStore.setTinySuccessToastMessage(t('research.queue.removed'))
          })
        }
      },
      alternativeAction: () => {
        const backend = new Backend()
        const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
        const index = currentQueue.indexOf(props.tech.id)
        if (index > -1) {
          currentQueue.splice(index, 1)
        }
        backend.updateTechQueue(currentQueue).then(() => {
          backend.setTechTarget(props.tech.id).then(() => {
            gameStore.setTinySuccessToastMessage(t('research.actions.techSetAsTarget'))
          })
        })
      }
    }
  } else if (gameStore.player?.currentTechId === 0) {
    const backend = new Backend()
    backend.setTechTarget(props.tech.id).then(() => {
      gameStore.setTinySuccessToastMessage(t('research.actions.targetSet'))
    })
  } else {
    gameStore.modal2Type = 'confirmationDelete'
    gameStore.modal2Data = {
      title: t('research.confirm.title'),
      message: t('research.confirm.switchMessage'),
      actionText: t('research.confirm.replace'),
      cancelText: t('research.confirm.none'),
      alternativeText: t('research.queue.addToQueue'),
      action: () => {
        const backend = new Backend()
        backend.setTechTarget(props.tech.id).then(() => {
          gameStore.setTinySuccessToastMessage(t('research.actions.targetSet'))
        })
      },
      alternativeAction: () => {
        const backend = new Backend()
        const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
        currentQueue.push(props.tech.id)
        backend.updateTechQueue(currentQueue).then(() => {
          gameStore.setTinySuccessToastMessage(t('research.queue.added'))
        })
      }
    }
  }
}

/**
 * Computed
 */
const status = computed(() => {
  if (gameStore.player?.currentTechId === props.tech.id) {
    return 'researching'
  }
  if (gameStore.playerTechs.has(props.tech.id)) {
    return 'researched'
  }
  if (props.tech.id === gameStore.player?.targetTechId) {
    return 'target'
  }
  const targetTech = assetStore.findTechById(gameStore.player!.targetTechId)
  if (targetTech && targetTech.requirementTree) {
    for (const requirementTechId of targetTech.requirementTree) {
      if (requirementTechId === props.tech.id) {
        return 'prerequisite'
      }
    }
  }

  if (gameStore.playerTechQueue.queue?.includes(props.tech.id)) {
    return 'queued'
  }
  return 'available'
})

const queuePosition = computed(() => {
  if (!gameStore.playerTechQueue.queue) return -1
  return gameStore.playerTechQueue.queue.indexOf(props.tech.id) + 1
})

const techName = computed(() => t(`research.techs.${props.tech.code}.name`))

const name = computed(() => {
  if (status.value === 'researching') {
    return (
      '<div class="flex justify-between text-cyan-500"><div>' +
      techName.value +
      '</div><div class="mr-2"></div></div>'
    )
  }
  if (status.value === 'researched') {
    return (
      '<div class="flex justify-between text-green-500"><div>' +
      techName.value +
      '</div><div class="mr-2"></div>'
    )
  }
  if (status.value === 'prerequisite') {
    return (
      '<div class="flex justify-between text-yellow-500"><div>' +
      techName.value +
      '</div><div class="mr-2"></div>'
    )
  }
  if (status.value === 'queued') {
    return (
      '<div class="flex justify-between text-white"><div>' +
      techName.value +
      '</div><div class="mr-2" title="' + t('research.queue.position', { position: queuePosition.value }) + '"><small>' +
      t('research.queue.positionShort', { position: queuePosition.value }) +
      '</small></div>'
    )
  }
  if (status.value === 'target') {
    return (
      '<div class="flex justify-between text-green-500"><div>' +
      techName.value +
      '</div><div class="mr-2"></div>'
    )
  }
  return techName.value
})

const provideIcons = computed<string[]>(() => {
  const icons: string[] = []

  for (const benefit of props.tech.provides) {
    if (benefit.type === 'buildings') {
      const effects = assetStore.getBuildingEffectsByCode(benefit.code) as any
      if (effects && Array.isArray(effects)) {
        for (const effect of effects) {
          if (effect.bonus > 0) {
            const type = CDN.normalize(effect.type)
            // Prevent duplicate icons
            if (!icons.includes(type)) {
              icons.push(type)
            }
          }
        }
      }
    } else if (benefit.type === 'ship_components') {
      // Show settle icon for colonization pods
      if (benefit.code === 'colonization_pod' || benefit.code === 'advanced_colonization_pod') {
        if (!icons.includes('cmd_settle')) {
          icons.push('cmd_settle')
        }
      }

      const effects = assetStore.getShipComponentEffectsByCode(benefit.code) as any
      if (effects && Array.isArray(effects)) {
        for (const effect of effects) {
          if (effect.modifier > 0) {
            if (!['ship_mass', 'ship_production_cost', 'ore'].includes(effect.type)) {
              const type = CDN.normalize(effect.type)
              if (!icons.includes(type)) {
                icons.push(type)
              }
            }
          }
        }
      }
    }
  }

  return icons
})

const isPrototype = computed(() => {
  // Check if this tech has been researched as a prototype by the player
  return gameStore.playerPrototypes.has(props.tech.id)
})

const effectiveCost = computed(() => {
  if (isPrototype.value && gameStore.player?.effectiveAllianceResearchCostMultiplier) {
    return Math.ceil(props.tech.cost * gameStore.player.effectiveAllianceResearchCostMultiplier)
  }
  return props.tech.cost
})

const currentResearchingTechEffectiveCost = computed(() => {
  if (gameStore.player?.currentTechId != props.tech.id) {
    return props.tech.cost
  }
  const tech = assetStore.findTechById(Number(gameStore.player?.currentTechId))
  if (!tech) {
    return props.tech.cost
  }
  
  // Use effective cost for currently researching tech if it's a prototype
  if (gameStore.playerPrototypes.has(tech.id) && gameStore.player?.effectiveAllianceResearchCostMultiplier) {
    return Math.ceil(tech.cost * gameStore.player.effectiveAllianceResearchCostMultiplier)
  }
  return tech.cost
})

const techPercentage = computed(() => {
  if (gameStore.player?.currentTechId != props.tech.id) {
    return -1
  }
  const tech = assetStore.findTechById(Number(gameStore.player?.currentTechId))
  if (!tech) {
    return 0
  }
  
  // Use effective cost for currently researching tech if it's a prototype
  let costToUse = tech.cost
  if (gameStore.playerPrototypes.has(tech.id) && gameStore.player?.effectiveAllianceResearchCostMultiplier) {
    costToUse = Math.ceil(tech.cost * gameStore.player.effectiveAllianceResearchCostMultiplier)
  }
  
  const percentage = Math.floor((Number(gameStore.player?.sciencePoints) / costToUse) * 100)
  if (percentage > 100) {
    return 100
  }
  return percentage
})

const eta = computed(() => {
  const eta = new Eta()
  // Use the effective cost for the current tech (whether it's being researched or not)
  let costForETA = effectiveCost.value
  
  // If this is the currently researching tech, use its specific effective cost
  if (gameStore.player?.currentTechId === props.tech.id) {
    costForETA = currentResearchingTechEffectiveCost.value
  }
  
  const calculatedEta = eta.techETA(costForETA)
  return calculatedEta < 0 ? 0 : calculatedEta
})

onMounted(() => {
  top.value = 3.5 + (props.tech.treeRow - 1) * 13
  left.value = 3 + (props.tech.treeColumn - 1) * 30
})
</script>

<template>
  <StandardCard
    :title="name"
    :style="'top:' + top + 'rem;left:' + left + 'rem'"
    :class="{
      'tech ': true,
      'opacity-40 hover:opacity-100': status === 'researched',
      'shadow-even-md shadow-cyan-600': status === 'researching',
      'shadow-even-md shadow-yellow-600': status === 'prerequisite',
      'shadow-even-md shadow-white': status === 'queued',
      'shadow-even-md shadow-green-600': status === 'target'
    }"
  >
    <ModalTrigger type="tech" :data="{ techId: props.tech.id }">
      <div class="flex">
        <img
          :src="CDN.techIcon(props.tech.code)"
          alt=""
          class="rounded shrink-0"
          style="width: 5rem !important; height: 5rem !important"
        />
        <div class="ps-2 flex flex-col justify-between w-full">
          <div class="flex justify-end">
            <template v-for="icon in provideIcons" :key="icon">
              <GenericIcon :type="icon" class="ml-2" />
            </template>
          </div>
          <div class="flex justify-end">
            <div v-if="techPercentage !== -1" class="w-full">
              <div v-if="eta != Infinity" class="text-end">{{ eta }} {{ pluralize(eta, t('research.week'), t('research.weeks')) }}</div>
              <div v-else class="text-end">{{ t('research.stalled') }}</div>
              <SimpleProgressBar
                :percentage="techPercentage"
                color="blue"
                :text="techPercentage + '%'"
                :height="5"
                :subtext="`${gameStore.player!.sciencePoints} / ${currentResearchingTechEffectiveCost}`"
              />
            </div>
            <div v-else class="flex items-center gap-2">
              <GenericIcon
                v-if="isPrototype"
                type="blueprint"
                class="opacity-70"
                :tooltip="t('research.prototype.discountTooltip', { percent: gameStore.player?.allianceResearchDiscountPercentage || 75 })"
              />
              <ActionButton
                v-if="!gameStore.playerTechs.has(props.tech.id) && status !== 'target'"
                @click.stop="setTech"
              >
                <GenericIconWithNumber
                  :amount="effectiveCost"
                  type="science"
                  text-color="text-slate-300"
                />
              </ActionButton>
            </div>
          </div>
        </div>
      </div>
    </ModalTrigger>
  </StandardCard>
</template>

<style scoped lang="scss">
.tech {
  position: absolute;
  width: 18rem;
}
</style>
